import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useApolloClient, useQuery } from '@apollo/client';
import { withRouter } from 'react-router';
import moment from 'moment';

import { Spinner } from 'admin-components/components';
import { bookingShape, historyShape, userShape } from 'constants/propTypes';
import { withBooking } from 'components/queries/GetBookingQuery';
import CheckableItems from 'components/ui/CheckableItems';
import { Column, Row } from 'components/shared/Layout';
import { GET_EXTRAS, GET_TICKET_DETAILS } from 'store/queries';
import { useManagedQuery } from 'utils/managedHooks';
import { getAssistances, getValidExtras } from 'utils/booking';
import GET_EXTENDED_COMPENSATION_BY_TICKET_NUMBERS from 'store/queries/GET_EXTENDED_COMPENSATION_BY_TICKET_NUMBERS';

import { StyledStickyContainer } from '../BookingDetails.styles';
import BookingDetailsTable from '../BookingDetailsTable';
import ModuleTabs from '../ModuleTabs';
import { bookingStatus } from '../../../types/booking';

import { bookingQuery } from './queries';
import Journeys from './Journeys';
import { UPDATE_PRODUCTS_WITH_SALESFORCE_STATUS } from 'store/mutations';

const Index = ({ booking, user, refetch, history }) => {
  const client = useApolloClient();

  const { data: dataExtras } = useManagedQuery(GET_EXTRAS, {
    variables: { reference: booking.reference }
  });

  const { ticketDetails, compensatedTickets } = useMemo(() => {
    const tickets = {
      ticketDetails: [],
      compensatedTickets: []
    };

    if (
      [bookingStatus.CONFIRMED, bookingStatus.OVERPAID, bookingStatus.UNDERPAID].includes(booking.status) &&
      (booking?.eurostarBookingItems?.length || booking?.greenspeedBookingItems?.length)
    ) {
      const activeBookingItems = [...(booking?.eurostarBookingItems || []), ...(booking?.greenspeedBookingItems || [])];

      [...booking.inbound, ...booking.outbound].forEach((leg) =>
        leg.products.forEach((product) => {
          if (activeBookingItems.some((item) => item.ticketNumber === product.ticketNumber)) {
            const departureDate = moment(leg.timing.isoDate);

            if (
              departureDate.startOf('day').isSameOrAfter(moment().startOf('day')) &&
              departureDate.startOf('day').isSameOrBefore(moment().startOf('day').add(14, 'days'))
            ) {
              tickets.ticketDetails.push(product.ticketNumber);
            }

            if (
              (product.isCheckedIn || (!product.cancelled && !product.provisional)) &&
              moment().isSameOrAfter(departureDate.add(12, 'hours'))
            ) {
              tickets.compensatedTickets.push(product.ticketNumber);
            }
          }
        })
      );
    }

    return tickets;
  }, [booking]);

  const { loading: getTicketDetailsLoading, data: getTicketDetailsData = {} } = useManagedQuery(GET_TICKET_DETAILS, {
    variables: {
      ticketNumbers: ticketDetails
    },
    skip: !ticketDetails?.length
  });

  const { loading: getCompensationDetailsLoading } = useQuery(GET_EXTENDED_COMPENSATION_BY_TICKET_NUMBERS, {
    variables: {
      ticketNumbers: compensatedTickets,
      bookingReference: booking.reference
    },
    skip: !compensatedTickets?.length,
    onCompleted: (data) => {
      if (data) {
        // Sync cached S3 products with Salesforce status
        client.mutate({
          mutation: UPDATE_PRODUCTS_WITH_SALESFORCE_STATUS,
          variables: {
            tickets: data.getExtendedCompensationByTicketNumbers
          }
        });
      }
    }
  });

  const journeyExtras = dataExtras && [...dataExtras.booking.outboundExtras, ...dataExtras.booking.inboundExtras];
  const assistances = journeyExtras && getAssistances(journeyExtras);

  const filteredExtras = dataExtras && [
    ...getValidExtras(dataExtras.booking.outboundExtras),
    ...getValidExtras(dataExtras.booking.insurances),
    ...getValidExtras(dataExtras.booking.hotels),
    ...getValidExtras(dataExtras.booking.inboundExtras),
    ...getValidExtras(dataExtras.booking.travelPassProducts)
  ];

  return (
    <CheckableItems>
      {(selectedItems, { isGroupChecked, toggleGroup }) => (
        <>
          {!getTicketDetailsLoading && !getCompensationDetailsLoading ? (
            <>
              <StyledStickyContainer id="booking-details">
                <BookingDetailsTable
                  booking={booking}
                  bookingLevelButtonsAreDisabled={
                    booking.allBookingItems.filter((bi) => !bi.cancelled).length !== selectedItems.length && selectedItems.length !== 0
                  }
                  ticketDetails={getTicketDetailsData.getTicketDetails}
                />
                <ModuleTabs
                  notes={booking.notes}
                  reference={booking.reference}
                  outbound={booking.nonExchangedOutbound}
                  inbound={booking.nonExchangedInbound}
                  assistances={assistances}
                  extras={filteredExtras}
                  booking={booking}
                />
              </StyledStickyContainer>
              <Journeys
                booking={booking}
                user={user}
                selectedItems={selectedItems}
                isGroupChecked={isGroupChecked}
                toggleGroup={toggleGroup}
                refetchBooking={refetch}
                ticketDetails={getTicketDetailsData.getTicketDetails}
                history={history}
              />
            </>
          ) : (
            <Row height="100vh">
              <Column alignItems="center" justifyContent="center">
                <Spinner />
              </Column>
            </Row>
          )}
        </>
      )}
    </CheckableItems>
  );
};

Index.propTypes = {
  booking: bookingShape,
  user: userShape,
  refetch: PropTypes.func.isRequired,
  history: historyShape
};

const options = {
  centerLoader: true
};

export default withRouter(withBooking(Index, bookingQuery, options));
